import { useState, useEffect } from "react";
import axios from "axios";
import { CalendarCheck } from "@phosphor-icons/react";
import { openBooking } from "@/lib/booking";

export const truckLevels = [
  { label: "1/8 Load", fraction: 1 / 8, yards: "~2 cu yds", price: 185, color: "#22c55e", fill: 12.5, examples: ["Small dresser", "Office chair", "10 moving boxes", "Small pile of bags"], icon: "📦" },
  { label: "1/4 Load", fraction: 1 / 4, yards: "~4 cu yds", price: 310, color: "#84cc16", fill: 25, examples: ["Sofa + coffee table", "TV stand", "5–6 bags of junk", "Small bedroom items"], icon: "🛋️" },
  { label: "1/3 Load", fraction: 1 / 3, yards: "~5 cu yds", price: 400, color: "#eab308", fill: 33, examples: ["Sectional sofa", "Mattress + frame", "Boxes + misc items", "Small garage cleanout"], icon: "🛏️" },
  { label: "1/2 Load", fraction: 1 / 2, yards: "~7.5 cu yds", price: 510, color: "#f97316", fill: 50, examples: ["Full bedroom set", "Mattress + dresser", "Desk + boxes", "Medium garage cleanout"], icon: "🏠" },
  { label: "2/3 Load", fraction: 2 / 3, yards: "~10 cu yds", price: 640, color: "#ef4444", fill: 66, examples: ["2-room cleanout", "Large furniture set", "Appliances + furniture", "Large garage cleanout"], icon: "🏡" },
  { label: "3/4 Load", fraction: 3 / 4, yards: "~11 cu yds", price: 760, color: "#dc2626", fill: 75, examples: ["Full garage cleanout", "3-room cleanout", "Multiple appliances", "Storage unit cleanout"], icon: "🏗️" },
  { label: "Full Load", fraction: 1, yards: "15 cu yds", price: 900, color: "#7c3aed", fill: 100, examples: ["Full apartment cleanout", "Large estate cleanout", "Entire garage + rooms", "Hoarder cleanout"], icon: "🚛" },
];

function IsuzuNPR({ fillPercent, color }) {
  const W = 1264, H = 848;                 // image native size
  // dump-bed footprint on /npr-boxtruck.jpg (image coords)
  const bedLeft = 415, bedRight = 1145, bedBottom = 576;
  const bedTop = 233;                       // bed lowered by ~1/3
  const IX0 = bedLeft + 18, IX1 = bedRight - 18, IW = IX1 - IX0;
  const F = bedBottom - 8;                   // floor baseline
  const fillW = Math.min(IW, (fillPercent / 100) * IW);
  const dimY = 748;
  const feetMap = { 12.5: 2, 25: 4, 33: 5, 50: 7, 66: 10, 75: 12, 100: 14 };
  const feet = feetMap[fillPercent] ?? Math.round((fillPercent / 100) * 14);
  const rnd = (n) => { const s = Math.sin(n * 127.1) * 43758.5453; return s - Math.floor(s); };

  // sketched mixed junk (appliances / furniture / boxes) filling the load area to the top
  const S = { fill: "none", stroke: "#dbe4ef", strokeWidth: 3.5, strokeLinejoin: "round", strokeLinecap: "round" };
  const drawers = [
    { w: 178, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 58} width="160" height="42" rx="10" /><rect x={x - 2} y={by - 92} width="164" height="40" rx="12" /><rect x={x - 10} y={by - 88} width="20" height="72" rx="8" /><rect x={x + 150} y={by - 88} width="20" height="72" rx="8" /><line x1={x + 80} y1={by - 86} x2={x + 80} y2={by - 20} /><line x1={x + 12} y1={by - 16} x2={x + 12} y2={by} /><line x1={x + 148} y1={by - 16} x2={x + 148} y2={by} /></g>) },
    { w: 74, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 64} width="64" height="64" rx="4" /><line x1={x} y1={by - 40} x2={x + 64} y2={by - 40} /><line x1={x + 32} y1={by - 64} x2={x + 32} y2={by - 40} /></g>) },
    { w: 72, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 118} width="60" height="118" rx="6" /><line x1={x} y1={by - 74} x2={x + 60} y2={by - 74} /><line x1={x + 48} y1={by - 108} x2={x + 48} y2={by - 84} /><line x1={x + 48} y1={by - 66} x2={x + 48} y2={by - 30} /></g>) },
    { w: 90, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 52} width="70" height="38" rx="8" /><rect x={x - 2} y={by - 82} width="74" height="34" rx="10" /><rect x={x - 8} y={by - 78} width="16" height="64" rx="7" /><rect x={x + 64} y={by - 78} width="16" height="64" rx="7" /><line x1={x + 6} y1={by - 14} x2={x + 6} y2={by} /><line x1={x + 64} y1={by - 14} x2={x + 64} y2={by} /></g>) },
    { w: 112, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 80} width="100" height="58" rx="6" /><rect x={x + 38} y={by - 22} width="24" height="10" /><line x1={x + 28} y1={by} x2={x + 72} y2={by} /></g>) },
    { w: 72, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 96} width="62" height="96" rx="6" /><circle cx={x + 31} cy={by - 50} r="22" /><line x1={x + 6} y1={by - 84} x2={x + 56} y2={by - 84} /></g>) },
    { w: 132, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 66} width="120" height="12" rx="3" /><line x1={x + 8} y1={by - 54} x2={x + 8} y2={by} /><line x1={x + 112} y1={by - 54} x2={x + 112} y2={by} /></g>) },
    { w: 60, el: (x, by, k) => (<g key={k} {...S}><polygon points={`${x + 6},${by - 100} ${x + 48},${by - 100} ${x + 40},${by - 66} ${x + 14},${by - 66}`} /><line x1={x + 27} y1={by - 66} x2={x + 27} y2={by - 8} /><line x1={x + 12} y1={by} x2={x + 42} y2={by} /></g>) },
    { w: 100, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 100} width="88" height="100" rx="4" /><line x1={x} y1={by - 66} x2={x + 88} y2={by - 66} /><line x1={x} y1={by - 33} x2={x + 88} y2={by - 33} /><circle cx={x + 44} cy={by - 83} r="3" /><circle cx={x + 44} cy={by - 50} r="3" /><circle cx={x + 44} cy={by - 16} r="3" /></g>) },
    { w: 44, el: (x, by, k) => (<g key={k} {...S}><rect x={x} y={by - 120} width="26" height="120" rx="13" /><ellipse cx={x + 13} cy={by - 120} rx="13" ry="6" /></g>) },
  ];
  const pieces = [];
  let k = 0;
  for (let by = F; by > bedTop + 28; by -= 92) {
    let x = IX0 + 6 + Math.floor(rnd(by) * 3) * 12;
    let idx = Math.floor(rnd(by * 1.7) * drawers.length);
    while (x < IX0 + fillW - 8) { const d = drawers[idx % drawers.length]; pieces.push(d.el(x, by, k++)); x += d.w + 12; idx++; }
  }

  return (
    // viewBox cropped to the truck (removes empty navy margins) so it renders ~30% bigger
    <svg viewBox="20 150 1226 650" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", maxHeight: 360 }}>
      <defs>
        <linearGradient id="steel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#aab3c0" /><stop offset="55%" stopColor="#64748b" /><stop offset="100%" stopColor="#3a4557" />
        </linearGradient>
        <linearGradient id="loadGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.6" /><stop offset="100%" stopColor={color} stopOpacity="0.32" />
        </linearGradient>
        <clipPath id="loadClip">
          <rect x={IX0 - 8} y={bedTop - 4} width={fillW + 14} height={bedBottom - bedTop + 4} />
        </clipPath>
      </defs>

      {/* the truck (cab + wheels + box) */}
      <image href="/npr-boxtruck.jpg" x="0" y="0" width={W} height={H} preserveAspectRatio="xMidYMid meet" />

      {/* erase the white box so the bed reads as an open/transparent body */}
      <rect x="414" y="176" width="744" height="404" fill="#0f172a" />

      {/* current load — tier color + sketched household items, filling front→rear */}
      <g clipPath="url(#loadClip)">
        <rect x={IX0} y={bedTop} width={Math.max(0, fillW)} height={bedBottom - bedTop} fill="url(#loadGrad)" style={{ transition: "width 0.5s cubic-bezier(.4,0,.2,1)" }} />
        {pieces}
      </g>

      {/* transparent side walls — full height to the top of the bed */}
      <rect x={bedLeft} y={bedTop} width={bedRight - bedLeft} height={bedBottom - bedTop} rx="4" fill="#94a3b8" fillOpacity="0.05" stroke="#94a3b8" strokeOpacity="0.5" strokeWidth="2.5" />
      <rect x={bedLeft} y={bedTop} width={bedRight - bedLeft} height="7" rx="2" fill="#c7d0dc" opacity="0.6" />

      {/* opaque front headboard (full height) + lip/apron at the top-left-front */}
      <rect x={bedLeft} y={bedTop} width="22" height={bedBottom - bedTop} rx="2" fill="url(#steel)" stroke="#2b3444" strokeWidth="2.5" />
      <polygon points={`${bedLeft + 22},${bedTop} ${bedLeft - 32},${bedTop - 18} ${bedLeft - 32},${bedTop - 2} ${bedLeft + 22},${bedTop + 16}`} fill="url(#steel)" stroke="#2b3444" strokeWidth="2.5" />
      <rect x={bedLeft - 32} y={bedTop - 18} width="56" height="6" rx="2" fill="#c7d0dc" opacity="0.7" />

      {/* rear tailgate (transparent, full height) */}
      <rect x={bedRight - 8} y={bedTop} width="16" height={bedBottom - bedTop} rx="2" fill="#94a3b8" fillOpacity="0.1" stroke="#94a3b8" strokeOpacity="0.5" strokeWidth="2.5" />

      {/* floor rail */}
      <rect x={bedLeft} y={bedBottom - 8} width={bedRight - bedLeft} height="10" rx="2" fill="url(#steel)" stroke="#2b3444" strokeWidth="1.5" />

      {fillPercent < 100 && (
        <g opacity="0.5" style={{ transition: "all 0.5s" }}>
          <line x1={IX0 + fillW + 14} y1={bedBottom - 70} x2={IX0 + fillW + 58} y2={bedBottom - 70} stroke={color} strokeWidth="5" strokeDasharray="9,6" />
          <polygon points={`${IX0 + fillW + 58},${bedBottom - 80} ${IX0 + fillW + 76},${bedBottom - 70} ${IX0 + fillW + 58},${bedBottom - 60}`} fill={color} />
        </g>
      )}

      {/* occupied-length dimension (front → fill edge) */}
      {(() => {
        const endX = IX0 + fillW;
        const lc = Math.min(Math.max((bedLeft + endX) / 2, bedLeft + 56), bedRight - 56);
        return (
          <g stroke="#94a3b8" strokeWidth="2">
            <line x1={bedLeft} y1={dimY} x2={endX} y2={dimY} />
            <line x1={bedLeft} y1={dimY - 13} x2={bedLeft} y2={dimY + 13} />
            <line x1={endX} y1={dimY - 13} x2={endX} y2={dimY + 13} />
            <polygon points={`${bedLeft + 2},${dimY} ${bedLeft + 18},${dimY - 7} ${bedLeft + 18},${dimY + 7}`} fill="#94a3b8" stroke="none" />
            <polygon points={`${endX - 2},${dimY} ${endX - 18},${dimY - 7} ${endX - 18},${dimY + 7}`} fill="#94a3b8" stroke="none" />
            <rect x={lc - 58} y={dimY - 17} width="116" height="34" rx="5" fill="#0f172a" stroke="none" />
            <text x={lc} y={dimY + 8} textAnchor="middle" fill="#e2e8f0" fontSize="26" fontWeight="700" stroke="none">{fillPercent >= 100 ? "14 ft" : `~${feet} ft`}</text>
          </g>
        );
      })()}
    </svg>
  );
}

const SERVICE_NOTES = {
  curbside: {
    title: "Curbside",
    text: "You stage items at a drive-up spot — driveway, open garage, or alley. Our crew loads the truck. You don't need to be home.",
  },
  full: {
    title: "Full-Service",
    text: "You point, we carry — from inside, upstairs, or the backyard. All labor is in the price, with 2+ crew for bulky pieces.",
  },
};

export default function QuoteTool() {
  const [selected, setSelected] = useState(0);
  const [levels, setLevels] = useState(truckLevels);
  const [discount, setDiscount] = useState(30);
  const [mode, setMode] = useState("curbside");
  const [animPrice, setAnimPrice] = useState(Math.round(truckLevels[0].price * 0.7));
  const [activeTab, setActiveTab] = useState("included");
  const level = levels[selected];
  const curbOf = (p) => Math.round(p * (1 - discount / 100));
  const priceOf = (p) => (mode === "curbside" ? curbOf(p) : p);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then(({ data }) => {
        const byLabel = {};
        (data?.tiers || []).forEach((t) => { byLabel[t.label] = t.price; });
        setLevels(truckLevels.map((t) => ({ ...t, price: byLabel[t.label] ?? t.price })));
        if (typeof data?.curbsideDiscount === "number") setDiscount(data.curbsideDiscount);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const target = priceOf(level.price);
    const start = animPrice;
    const diff = target - start;
    const steps = 20;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setAnimPrice(Math.round(start + (diff * step) / steps));
      if (step >= steps) clearInterval(interval);
    }, 15);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, levels, mode, discount]);

  return (
    <div data-testid="quote-tool" style={{ background: "linear-gradient(135deg, #0f172a 0%, #15306E 100%)", borderRadius: 20, fontFamily: "inherit", color: "white", padding: "24px 16px" }}>

      {/* Header */}
      <div className="text-center" style={{ marginBottom: 18 }}>
        <p style={{ color: "#cbd5e1", fontSize: 15, margin: 0, fontWeight: 700 }}>San Diego's Transparent Junk Removal Pricing</p>
        <p style={{ color: "#64748b", fontSize: 12, margin: "3px 0 0" }}>15 Cu Yd • No Hidden Fees</p>
      </div>

      {/* Truck Visual */}
      <div style={{ background: "#0f172a", borderRadius: 16, padding: "14px 8px 6px", marginBottom: 16, border: "1px solid #1e293b" }}>
        <IsuzuNPR fillPercent={level.fill} color={level.color} />
        <div style={{ textAlign: "center", fontSize: 11, color: "#475569", marginTop: 2 }}>← Loading fills front-to-rear</div>
      </div>

      {/* Service type toggle */}
      <div data-testid="service-mode-toggle" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, background: "#0f172a", padding: 5, borderRadius: 12, marginBottom: 14, border: "1px solid #1e293b" }}>
        {[["curbside", `Curbside · Save ${discount}%`], ["full", "Full-Service"]].map(([key, label]) => (
          <button key={key} data-testid={`mode-${key}`} onClick={() => setMode(key)} style={{
            padding: "10px 6px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 800,
            background: mode === key ? level.color : "transparent", color: mode === key ? "white" : "#94a3b8",
            transition: "background 0.2s, color 0.2s",
          }}>{label}</button>
        ))}
      </div>

      {/* Price Display */}
      <div className="text-center" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 2, textTransform: "uppercase", letterSpacing: 2 }}>Estimated {SERVICE_NOTES[mode].title} Price</div>
        <div data-testid="estimate-price" style={{ fontSize: 60, fontWeight: 900, color: level.color, lineHeight: 1, transition: "color 0.3s" }}>${animPrice}</div>
        <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>{level.label} • {level.yards}</div>
      </div>

      {/* Both prices side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
        {[["curbside", curbOf(level.price)], ["full", level.price]].map(([key, p]) => (
          <button key={key} data-testid={`price-compare-${key}`} onClick={() => setMode(key)} style={{
            textAlign: "left", background: "#1e293b", borderRadius: 12, padding: "10px 12px", cursor: "pointer", color: "white",
            border: mode === key ? `2px solid ${level.color}` : "2px solid #334155", transition: "border-color 0.2s",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: mode === key ? level.color : "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>{SERVICE_NOTES[key].title}</span>
              <span data-testid={`price-${key}`} style={{ fontSize: 18, fontWeight: 900 }}>${p}</span>
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, lineHeight: 1.4 }}>{SERVICE_NOTES[key].text}</div>
          </button>
        ))}
      </div>

      {/* Load Selector Buttons */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5, marginBottom: 14 }}>
        {levels.map((lvl, i) => (
          <button key={i} data-testid={`load-button-${i}`} onClick={() => setSelected(i)} style={{
            background: selected === i ? lvl.color : "#1e293b",
            border: selected === i ? `2px solid ${lvl.color}` : "2px solid #334155",
            borderRadius: 10, padding: "8px 3px", cursor: "pointer",
            color: selected === i ? "white" : "#94a3b8",
            fontWeight: selected === i ? 800 : 500, fontSize: 10,
            transition: "background 0.2s, border 0.2s, color 0.2s", textAlign: "center",
          }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>{lvl.icon}</div>
            <div>{lvl.label}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: selected === i ? "white" : lvl.color, marginTop: 2 }}>${priceOf(lvl.price)}</div>
          </button>
        ))}
      </div>

      {/* Slider */}
      <div style={{ marginBottom: 20 }}>
        <input type="range" min={0} max={6} value={selected} data-testid="load-slider" aria-label="Estimated truck load size" title="Estimated truck load size" onChange={(e) => setSelected(Number(e.target.value))}
          style={{ width: "100%", accentColor: level.color, height: 6, cursor: "pointer" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8", marginTop: 3 }}>
          <span>1/8 Load</span><span>← Drag to select →</span><span>Full Load</span>
        </div>
      </div>

      <button type="button" data-testid="load-book-button"
        onClick={() => openBooking({ booking_type: "load", service_type: mode, load_label: `${level.label} (${level.yards})`, estimate: priceOf(level.price) })}
        className="w-full flex items-center justify-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm py-3.5 rounded-lg transition-colors mb-5">
        <CalendarCheck size={18} weight="fill" /> Book this load · ${priceOf(level.price)}
      </button>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["included", "✅ Included"], ["waste", "⚠️ Waste Policy"]].map(([key, label]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setActiveTab(key)} style={{
            flex: 1, padding: "9px 4px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
            background: activeTab === key ? level.color : "#1e293b",
            color: activeTab === key ? "white" : "#94a3b8",
            transition: "background 0.2s, color 0.2s",
          }}>{label}</button>
        ))}
      </div>

      {/* Tab: Included */}
      {activeTab === "included" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ background: "#1e293b", borderRadius: 12, padding: 14, border: "1px solid #22c55e40" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#22c55e", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>✅ Always Included</div>
            {["Professional hauling", "All loading & hauling", "Disposal fees", "Post-job sweep", "Donation drop-off", "Licensed & insured", "Same-day available"].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, fontSize: 12, color: "#cbd5e1" }}>
                <span style={{ color: "#22c55e", fontSize: 10 }}>✓</span> {item}
              </div>
            ))}
          </div>
          <div style={{ background: "#1e293b", borderRadius: 12, padding: 14, border: "1px solid #f9731640" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#f97316", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>💰 Extra Charges</div>
            {["2+ flights of stairs", "Long carry (>75 ft)", "Disassembly required", "Hot tub removal", "Piano removal", "Shed demolition", "Construction debris"].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, fontSize: 12, color: "#cbd5e1" }}>
                <span style={{ color: "#f97316", fontSize: 10 }}>+</span> {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Waste Policy */}
      {activeTab === "waste" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <div style={{ background: "#1c0a0a", borderRadius: 12, padding: 16, border: "2px solid #dc2626" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>🚫</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#ef4444" }}>Hazardous Waste — NOT Accepted</div>
                <div style={{ fontSize: 11, color: "#9f1239" }}>We are unable to legally transport these materials</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[["🛢️", "Motor Oil"], ["❄️", "Coolants / Antifreeze"], ["🔋", "Car Batteries"], ["🧪", "Chemicals / Solvents"], ["🎨", "Paint / Stains"], ["⛽", "Gasoline / Fuels"], ["💨", "Propane Tanks"], ["☠️", "Pesticides / Herbicides"]].map(([icon, label]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, background: "#2d0a0a", borderRadius: 8, padding: "7px 10px" }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 12, color: "#fca5a5" }}>{label}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "#fca5a5", background: "#2d0a0a", borderRadius: 8, padding: "8px 12px" }}>
              ⚠️ For hazardous waste, contact SD County HHW: <span style={{ color: "#ef4444" }}>(858) 694-7000</span>
            </div>
          </div>

          <div style={{ background: "#0c1a2e", borderRadius: 12, padding: 16, border: "2px solid #0ea5e9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>💻</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#38bdf8" }}>E-Waste — Accepted (Review Required)</div>
                <div style={{ fontSize: 11, color: "#7dd3fc" }}>Must be reviewed & approved prior to pickup</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
              {[["🖥️", "Monitors / TVs"], ["💻", "Laptops / Computers"], ["🖨️", "Printers / Scanners"], ["📱", "Phones / Tablets"], ["🎮", "Gaming Consoles"], ["📷", "Cameras / Electronics"]].map(([icon, label]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, background: "#0f2744", borderRadius: 8, padding: "7px 10px" }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 12, color: "#bae6fd" }}>{label}</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#0f2744", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#38bdf8", marginBottom: 4 }}>📋 How It Works:</div>
              {["1. Text us a photo of your e-waste items", "2. We review condition & type before confirming", "3. Accepted items are recycled responsibly", "4. Some items may carry a small handling fee"].map((step, i) => (
                <div key={i} style={{ fontSize: 12, color: "#7dd3fc", marginBottom: 3 }}>{step}</div>
              ))}
            </div>
          </div>

          <div style={{ background: "#1c1917", borderRadius: 12, padding: 14, border: "1px solid #78350f" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#f97316", marginBottom: 6 }}>⚠️ Heavy Material Surcharge</div>
            <p style={{ fontSize: 12, color: "#a8a29e", margin: "0 0 8px" }}>Dense construction materials require additional disposal fees:</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {["Concrete", "Dirt/Soil", "Roofing", "Brick", "Tile", "Asphalt", "Gravel", "Sand"].map((m) => (
                <span key={m} style={{ background: "#292524", border: "1px solid #78350f", borderRadius: 6, padding: "3px 9px", fontSize: 11, color: "#f97316" }}>{m}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
